import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, Tag, ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../api";
import { useI18n, useL } from "../i18n";
import useSocialMeta from "../hooks/useSocialMeta";
import SocialShare from "./SocialShare";
import AdSlot from "./AdSlot";

// Ultra-lightweight markdown-ish renderer: H2, tables, unordered lists, bold, links.
function renderMarkdown(md) {
  const lines = md.split(/\r?\n/);
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Table detection (line has |)
    if (line.startsWith("|") && lines[i + 1]?.match(/^\|[\s-|]+\|$/)) {
      const header = line.split("|").slice(1, -1).map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      out.push(
        <div key={`t-${out.length}`} className="overflow-x-auto -mx-2 my-4">
          <table className="min-w-full text-sm rounded-2xl overflow-hidden bg-white border border-[#E6DED3]">
            <thead className="bg-[#F3EDE4]">
              <tr>
                {header.map((h, hi) => (
                  <th key={hi} className="px-3 py-2 text-start font-medium text-[#1C1917]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-t border-[#E6DED3]">
                  {r.map((c, ci) => (
                    <td key={ci} className="px-3 py-2 text-[#57534E]">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.startsWith("## ")) {
      out.push(<h2 key={`h-${out.length}`} className="font-display text-2xl text-[#1C1917] mt-8 mb-3">{line.slice(3)}</h2>);
      i++; continue;
    }
    if (line.startsWith("- ") || line.match(/^\d+\. /)) {
      const items = [];
      const isNumbered = !!line.match(/^\d+\. /);
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].match(/^\d+\. /))) {
        const raw = lines[i].replace(/^(- |\d+\. )/, "");
        items.push(raw);
        i++;
      }
      const Tag = isNumbered ? "ol" : "ul";
      out.push(
        <Tag key={`l-${out.length}`} className={`my-4 ps-6 space-y-1.5 text-[#1C1917] ${isNumbered ? "list-decimal" : "list-disc"}`}>
          {items.map((it, ii) => <li key={ii} dangerouslySetInnerHTML={{ __html: inlineFmt(it) }} />)}
        </Tag>
      );
      continue;
    }
    if (line.trim().length === 0) { i++; continue; }
    out.push(
      <p key={`p-${out.length}`} className="my-3 text-[#1C1917] leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFmt(line) }} />
    );
    i++;
  }
  return out;
}

// bold **x** only — safest against XSS since content is our own
function inlineFmt(text) {
  return String(text)
    .replace(/[<>]/g, (c) => (c === "<" ? "&lt;" : "&gt;"))
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export function BlogIndex() {
  const { lang, t } = useI18n();
  const L = useL();
  const [posts, setPosts] = useState([]);

  useSocialMeta({
    title: L({ ar: "مدوّنة رحّال — نصائح السفر في الشرق الأوسط", en: "Rahhal Blog — Middle East Travel Tips", fr: "Blog Rahhal — Astuces voyage Moyen-Orient" }),
    description: L({ ar: "مقالات دورية عن الوجهات والأسعار ونصائح السفر في المنطقة العربية.", en: "Regular articles on destinations, prices and travel tips in the Arab world.", fr: "Articles réguliers sur destinations, prix et astuces voyage dans le monde arabe." }),
  });

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch((err) => console.warn("posts fetch failed", err));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-index">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-[#C85A32] font-semibold">
          {L({ ar: "المدوّنة", en: "Blog", fr: "Blog" })}
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl text-[#1C1917]">
          {L({ ar: "قصص وأدلّة السفر", en: "Travel stories & guides", fr: "Récits et guides de voyage" })}
        </h1>
        <p className="mt-3 text-[#57534E] max-w-2xl">
          {L({
            ar: "مقالات جديدة كل أسبوع — أرخص الوجهات، مقارنات، نصائح، ودليل الأكل.",
            en: "New articles every week — cheapest destinations, comparisons, tips and food guides.",
            fr: "Nouveaux articles chaque semaine — destinations, comparatifs, astuces, gastronomie."
          })}
        </p>
      </div>

      <AdSlot slotId={process.env.REACT_APP_ADSENSE_SLOT_HOME_BANNER} layout="banner" className="mb-8" />

      {posts.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-80 bg-[#F3EDE4] rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
          {posts.map((p) => {
            const title = { ar: p.title_ar, en: p.title_en, fr: p.title_fr }[lang] || p.title_en;
            const excerpt = { ar: p.excerpt_ar, en: p.excerpt_en, fr: p.excerpt_fr }[lang] || p.excerpt_en;
            return (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="hoverable-card group bg-white rounded-3xl overflow-hidden border border-[#E6DED3] flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#F3EDE4]">
                  <img src={p.cover} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#78716C]">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(p.published).toLocaleDateString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-US")}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{p.read_minutes} {L({ ar: "دقائق", en: "min", fr: "min" })}</span>
                  </div>
                  <h3 className="mt-3 font-display text-lg text-[#1C1917] leading-tight">{title}</h3>
                  <p className="mt-2 text-sm text-[#57534E] line-clamp-3 flex-1">{excerpt}</p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const L = useL();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const Arrow = lang === "ar" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    setPost(null); setError(null);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setError("not_found"));
  }, [slug]);

  const title = post ? ({ ar: post.title_ar, en: post.title_en, fr: post.title_fr }[lang] || post.title_en) : "";
  const excerpt = post ? ({ ar: post.excerpt_ar, en: post.excerpt_en, fr: post.excerpt_fr }[lang] || post.excerpt_en) : "";
  const body = post ? ({ ar: post.body_ar, en: post.body_en, fr: post.body_fr }[lang] || post.body_en) : "";

  useSocialMeta({
    title: title ? `${title} · Rahhal` : undefined,
    description: excerpt,
    image: post?.cover,
  });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-[#78716C]">{L({ ar: "المقال غير موجود", en: "Post not found", fr: "Article introuvable" })}</p>
        <Link to="/blog" className="mt-4 inline-flex px-5 py-2.5 rounded-full bg-[#1C1917] text-[#FAF7F2] text-sm">
          {L({ ar: "كل المقالات", en: "All posts", fr: "Tous les articles" })}
        </Link>
      </div>
    );
  }
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading…</div>;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="blog-post">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#C85A32]">
        <Arrow className="h-4 w-4" />
        {L({ ar: "المدوّنة", en: "Blog", fr: "Blog" })}
      </Link>

      <div className="mt-4 aspect-[16/9] rounded-3xl overflow-hidden bg-[#F3EDE4]">
        <img src={post.cover} alt={title} className="w-full h-full object-cover" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-[#78716C]">
        <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(post.published).toLocaleDateString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.read_minutes} {L({ ar: "دقائق قراءة", en: "min read", fr: "min de lecture" })}</span>
        <span>· {post.author}</span>
      </div>

      <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-[#1C1917] leading-tight">{title}</h1>
      <p className="mt-4 text-lg text-[#57534E] leading-relaxed">{excerpt}</p>

      <div className="mt-6"><SocialShare text={title} testidPrefix="blog-share" /></div>

      <div className="mt-8 prose max-w-none text-base">{renderMarkdown(body)}</div>

      <AdSlot slotId={process.env.REACT_APP_ADSENSE_SLOT_DETAIL_INLINE} layout="in-article" className="mt-10" />

      {(post.tags_ar || post.tags_en) && (
        <div className="mt-8 flex flex-wrap gap-2">
          {(lang === "ar" ? (post.tags_ar || []) : (post.tags_en || [])).map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F3EDE4] text-xs text-[#57534E]">
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
